/* eslint-disable camelcase */

// node modules
import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// app modules: components
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import BookRankOverlay from 'components/BookRankOverlay';
import BookStatus from 'components/BookStatus';
import Heading from 'components/Heading';
import Image from 'components/Image';
import Link from 'components/Link';

// app modules: services
import assets from 'services/assets';
import route from 'services/route';
import { isApp } from 'services/environment';

// assets
import CssAuthorBooks from './AuthorBooks.scss';

const FIELDS = gql`
  fragment AuthorBookFields on Book {
    alacarte
    coming_soon
    indexable
    image_name
    isbn
    licensed
    ranking_recommendations
    recommendations_count
    signature_
    title_short
  }
`;

const GET_PERSON_BOOKS_QUERY = gql`
  query GetAuthorBooks($id: String, $includeTags: [String], $country: String) {
    Person(id: $id) {
      id
      books(includeTags: $includeTags) {
        alacarte_available(country: $country)
        ...AuthorBookFields
      }
    }
  }
  ${FIELDS}
`;

const GET_PERSON_CONTRIBUTIONS_QUERY = gql`
  query GetAuthorAppearsIn($id: String, $includeTags: [String], $country: String) {
    Person(id: $id) {
      id
      appearsIn(includeTags: $includeTags) {
        alacarte_available(country: $country)
        ...AuthorBookFields
      }
    }
  }
  ${FIELDS}
`;

function AuthorBooks(props) {
  const { authorId, heading, exclude, as } = props;
  useStyles(CssAuthorBooks);
  const includeTags = useSelector(state =>
    state.user.oven_brands?.map(brand => brand.toLowerCase()),
  );
  const userLoaded = useSelector(state => state.user.logged_in === false || state.user.id > 0);
  const country = useSelector(state => state.user.geolocation.countryCode);

  const { loading, error, data } = useQuery(
    as === 'contributor' ? GET_PERSON_CONTRIBUTIONS_QUERY : GET_PERSON_BOOKS_QUERY,
    {
      variables: { id: authorId, includeTags, country },
      ssr: false,
      skip: !userLoaded,
      fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    },
  );

  const renderBooks = books =>
    books.map((book, bookIndex) => {
      const key = `book-of-${authorId}-${bookIndex}`;
      const { image_name, isbn, ranking_recommendations, signature_, title_short } = book;
      const srcSet = assets.srcSet(
        image_name,
        [{ w: 100, h: 100, resize: 'fit-in' }, { w: 200, h: 200, resize: 'fit-in' }],
        assets.coverUrl,
      );
      return (
        <div
          key={key}
          className={classNames('author-books__book', {
            'author-books__book--single': books.length === 1,
          })}
        >
          <BookStatus statusType="small" book={book} />
          <Link to={route.book(isbn, signature_)} className="author-books__book__cover">
            <Image
              overlay={<BookRankOverlay ranking={ranking_recommendations} />}
              image={assets.coverUrl(image_name, {
                resize: 'fit-in',
                width: 100,
                height: 100,
              })}
              srcSet={srcSet}
              height={100}
              alt={title_short}
              className="author-books__book__figure"
            >
              {book.alacarte ? (
                <AlacarteStatusIndicator
                  available={book.alacarte_available}
                  isbn={isbn}
                  signature_={signature_}
                  className="author-books__book__alacarte-badge"
                />
              ) : null}
            </Image>
          </Link>
          <p className="author-books__book__title">
            <Link to={route.book(isbn, signature_)}>{title_short}</Link>
          </p>
        </div>
      );
    });

  if (loading) {
    // We could do a loading skeleton here
    return null;
  }
  if (error) {
    return <div>{error.message}</div>;
  }

  if (data?.Person) {
    const books = data.Person.books || data.Person.appearsIn;
    if (books && books.length) {
      const filteredBooks = books.filter(book => exclude.indexOf(book.isbn) === -1);

      if (filteredBooks.length > 0) {
        return (
          <div className="author-books">
            <Heading withDash className="margin-bottom-20">
              {heading}
            </Heading>
            <div className="author-books__books">{renderBooks(filteredBooks)}</div>
          </div>
        );
      }
    }
  }
  return null;
}

AuthorBooks.propTypes = {
  authorId: PropTypes.string,
  exclude: PropTypes.arrayOf(PropTypes.string),
  heading: PropTypes.string,
  as: PropTypes.oneOf(['author', 'contributor']),
};

AuthorBooks.defaultProps = {
  authorId: null,
  exclude: [],
  heading: '',
  as: 'author',
};

export default AuthorBooks;
